import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALE_CODES, getLocaleMeta } from './locales';
import { getLocale } from '../stores/useLocaleStore';

// Eager namespaces only (D8). Everything else is registered on demand by
// loadNamespace() below, which Vite code-splits per (locale, namespace), so
// adding a language does not grow the initial chunk.
import enCommon from '../locales/en/common.json';
import enApp from '../locales/en/app.json';

// Validation messages live under common's `validation.*` subtree rather than
// in a namespace of their own — see helpers/validationMessages.js. `common` is
// eager, which is the property that mattered: a field error has to be
// renderable the instant a form mounts.
export const EAGER_NS = ['common', 'app'];

i18n.use(initReactI18next).init({
  lng: getLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: LOCALE_CODES,
  ns: EAGER_NS,
  defaultNS: 'common',
  resources: { en: { common: enCommon, app: enApp } },
  // D4: the English text lives at the call site and is the terminal fallback,
  // so a missing key renders English, never a raw dot-path. This is what makes
  // the rollout incremental — a module with no translations yet is not broken,
  // it is English.
  parseMissingKeyHandler: (key, defaultValue) => defaultValue ?? key,
  interpolation: {
    // React already escapes. Double-escaping turns the apostrophe in
    // "l'installazione" into &#39; on screen.
    escapeValue: false,
  },
  returnNull: false,
  // The other half of D4, and NOT the default (i18next ships `true`).
  //
  // The extractor writes `""` for every key a non-primary locale has not
  // translated yet — that is by design (i18next.config.mjs), on the stated
  // premise that an empty value "falls back through the chain to English".
  // It only does so with this flag: with `returnEmptyString: true`, `""` is a
  // legitimate translation and i18next returns it verbatim, so the fallback
  // chain never runs and `parseMissingKeyHandler` never fires — a key is only
  // "missing" when absent, never when present-and-empty.
  //
  // The visible symptom is a surface that renders BLANK in a partially
  // translated locale rather than English: the whole onboarding widget went
  // empty in es/it/de/... the moment its last hardcoded string became a key.
  // 214 es values are empty today; `en` has none, so nothing legitimately
  // relies on an empty string being returned.
  returnEmptyString: false,
  // We own the loading gate (useNamespace renders English defaults during the
  // first paint of a not-yet-loaded namespace). Suspense here would blank the
  // page instead.
  react: { useSuspense: false },
});

// Vite resolves this glob at build time into a map of lazy importers, one chunk
// per (locale, namespace). New JSON files are picked up with no registration
// step — which is what makes D7's "add a folder" true.
//
// en-XA is EXCLUDED here and loaded separately below. The glob reads the
// filesystem, not SUPPORTED_LOCALES, so without this exclusion a developer who
// had run `npm run i18n:pseudo` would ship accented QA strings to customers in
// the production bundle — unreachable, but present and served. Caught by
// inspecting the emitted chunks; the pseudo-locale's absence from the prod
// locale list is not enough on its own.
const resources = import.meta.glob(['../locales/*/*.json', '!../locales/en-XA/*.json']);

// Every namespace present on disk, derived from the glob so D7's "add a folder"
// stays true. Used to fully preload a non-English locale — see applyLocale.
const ALL_NS = [
  ...new Set(
    Object.keys(resources)
      .map((k) => k.match(/\/([^/]+)\.json$/)?.[1])
      .filter(Boolean),
  ),
];

// Dev/QA only. `import.meta.env.PROD` is statically replaced by Vite, so
// Rollup eliminates this branch AND the dynamic imports inside it from a
// production build — verified by grepping the emitted assets for the ⟦ marker.
const pseudoResources = import.meta.env.PROD ? {} : import.meta.glob('../locales/en-XA/*.json');

const loaded = new Set(EAGER_NS.map((ns) => `en:${ns}`));

export async function loadNamespace(lng, ns) {
  const id = `${lng}:${ns}`;
  if (loaded.has(id)) return;
  const key = `../locales/${lng}/${ns}.json`;
  const importer = resources[key] ?? pseudoResources[key];
  // A namespace with no translation file for this locale is NORMAL during the
  // rollout: that locale falls back to English. Never throw here.
  if (!importer) {
    loaded.add(id);
    return;
  }
  const mod = await importer();
  i18n.addResourceBundle(lng, ns, mod.default, true, true);
  loaded.add(id);
}

// Registered by src/index.jsx so applyLocale can clear the React Query cache
// without this module importing the query client (which would pull the whole
// data layer into the i18n chunk).
let onLocaleChange = null;
export const setLocaleChangeHandler = (fn) => {
  onLocaleChange = fn;
};

// Switch everything that has an opinion about language, in one place.
//
// Order matters: resources and the dayjs locale must be in memory BEFORE
// changeLanguage fires, or every subscribed component re-renders once against
// missing resources and the UI visibly flickers through English.
export async function applyLocale(code) {
  const previous = i18n.language;
  const meta = getLocaleMeta(code);
  // English needs no bundle load — every t() call carries its English default
  // (D4), so only the eager namespaces (which also host the cross-cutting keys)
  // must be present. Any OTHER locale must preload ALL namespaces here: the app
  // uses plain useTranslation everywhere (never the useNamespace lazy gate), so
  // nothing else pulls a module's lazy bundle, and its keys would otherwise fall
  // back to English on screen even though the translation exists on disk.
  const namespaces = code === DEFAULT_LOCALE ? EAGER_NS : ALL_NS;
  await Promise.all([...namespaces.map((ns) => loadNamespace(code, ns)), meta.dayjs()]);
  await i18n.changeLanguage(code);
  // Invariant: <html lang> tracks the active locale. Screen readers, CSS
  // :lang() hyphenation and Chrome Translate's offer heuristic all key off it.
  // index.html ships lang="en"; this is what makes that a boot default rather
  // than a lie.
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', code);
  }
  onLocaleChange?.(code, previous);
}

// The locale's Clerk localization bundle (esES / itIT / undefined for en).
// Resolved lazily so @clerk/localizations' full catalogue never enters the
// initial chunk.
export const loadClerkLocalization = (code) => getLocaleMeta(code).clerk();

export default i18n;
